/**
 * SpendChart — the last 30 days of priced spend as one thin line on a
 * faint grid (design-principles §3), drawn with recharts like the eval
 * score chart, so the axis sits inside the plot and every day is
 * hoverable. The line breaks at days the gateway could not price, and a
 * dashed rule marks each day with unpriced traffic, in full or in part,
 * so unpriced usage never reads as a $0 day. Data shaping lives in
 * billing.ts and is tested there.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts'

import type { BillingSpendDay } from '@/api/generated/model'
import {
  formatDay,
  formatUsd,
  spendPointLabel,
  spendSeries,
  type SpendPoint,
} from '@/components/billing/billing'
import { CHART_INK, CHART_REFERENCE_DASH, CHART_REFERENCE_STROKE } from '@/components/evals/chartStyle'

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }

function SpendTooltip({ tooltip }: { tooltip: TooltipContentProps }): React.JSX.Element | null {
  const { active, payload } = tooltip
  const entry = payload.find((item) => item.dataKey === 'spend')
  if (!active || entry === undefined) return null
  const point = entry.payload as SpendPoint
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="rounded-lg border border-border bg-card p-2 text-xs shadow-sm"
    >
      <p className="text-muted-foreground">{formatDay(`${point.date}T00:00:00Z`)}</p>
      <p className="mt-1 text-foreground">
        Requests <span className="font-mono tabular-nums">{point.requests.toLocaleString('en-US')}</span>
      </p>
      <p className="text-foreground">
        Spend <span className="font-mono tabular-nums">{spendPointLabel(point)}</span>
      </p>
    </div>
  )
}

export function SpendChart({ days }: { days: BillingSpendDay[] }): React.JSX.Element {
  const series = spendSeries(days)
  const unpricedDays = series.filter((point) => point.unpriced)
  return (
    <div className="h-52 w-full" role="img" aria-label="Daily spend over the last 30 days">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border) / 0.6)" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(date: string) => series.find((point) => point.date === date)?.label ?? date}
            interval="preserveStartEnd"
            minTickGap={28}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
          />
          <YAxis
            dataKey="spend"
            tickFormatter={(value: number) => formatUsd(value)}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={56}
            allowDecimals
          />
          <Tooltip
            cursor={{ stroke: 'hsl(var(--border))' }}
            content={(props) => <SpendTooltip tooltip={props} />}
          />
          {unpricedDays.map((point) => (
            <ReferenceLine
              key={point.date}
              x={point.date}
              stroke={CHART_REFERENCE_STROKE}
              strokeDasharray={CHART_REFERENCE_DASH}
            />
          ))}
          <Line
            type="linear"
            dataKey="spend"
            stroke={CHART_INK}
            strokeWidth={1.25}
            dot={{ r: 1.5, fill: CHART_INK, strokeWidth: 0 }}
            activeDot={{ r: 3, fill: CHART_INK, strokeWidth: 0 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
